import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TargetPicker } from "@/components/bulk/TargetPicker";
import { Check, Clock, Cpu, FileText, Loader2, Undo2, Ban, Truck, Search } from "lucide-react";

const STATUS = {
  ready: { label: "Ready — waiting for your Save", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  needs_you: { label: "Choose where it goes", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  no_match: { label: "No exact VIN match — not placed", cls: "bg-red-50 text-red-800 border-red-200" },
  refused: { label: "Not taken", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  saved: { label: "Saved", cls: "bg-emerald-600 text-white border-emerald-600" },
  waiting: { label: "On Waiting Dataplates", cls: "bg-sky-50 text-sky-800 border-sky-200" },
  undone: { label: "Undone", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const Option = ({ o, picked, onPick, testid }) => (
  <button type="button" data-testid={testid} onClick={() => onPick(o)}
    className={`w-full text-left rounded-lg border px-3 py-2 flex items-start gap-2 transition-colors ${picked ? "border-[#B01E28] bg-red-50/50" : "border-slate-200 hover:border-slate-400"}`}>
    <span className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${picked ? "border-[#B01E28] bg-[#B01E28]" : "border-slate-300"}`} />
    {o.kind === "part" ? <Cpu className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" /> : <Truck className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />}
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-slate-800">{o.kind === "part" ? "Engine part" : "Truck"} · {o.label}</span>
      {o.why && <span className="block text-[11px] text-slate-500">{o.why}</span>}
    </span>
  </button>
);

const Choices = ({ f, choice, setChoice, setPicker }) => {
  const opts = [...(f.suggestions || [])];
  if (choice && !opts.some((o) => o.kind === choice.kind && o.id === choice.id)) opts.push(choice);
  return (
    <div className="space-y-1.5">
      {opts.map((o, i) => (
        <Option key={`${o.kind}-${o.id}`} o={o} picked={choice?.kind === o.kind && choice?.id === o.id}
          onPick={setChoice} testid={`bulk-file-option-${f.id}-${i}`} />
      ))}
      {f.kind === "dataplate" && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" size="sm" variant="outline" data-testid={`bulk-file-pick-truck-${f.id}`} onClick={() => setPicker("truck")}
            className="h-8 rounded-lg text-xs"><Search className="w-3.5 h-3.5 mr-1" />Pick a truck</Button>
          <Button type="button" size="sm" variant="outline" data-testid={`bulk-file-pick-part-${f.id}`} onClick={() => setPicker("part")}
            className="h-8 rounded-lg text-xs"><Search className="w-3.5 h-3.5 mr-1" />Pick an engine part</Button>
        </div>
      )}
      {f.kind === "sheet_scan" && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" size="sm" variant="outline" data-testid={`bulk-file-pick-truck-${f.id}`} onClick={() => setPicker("truck")}
            className="h-8 rounded-lg text-xs"><Search className="w-3.5 h-3.5 mr-1" />{(f.suggestions || []).length ? "Pick a different truck" : "Pick the truck"}</Button>
        </div>
      )}
    </div>
  );
};

const Preview = ({ f }) => (
  <>
    {f.cost_note && <p data-testid={`bulk-file-cost-${f.id}`} className="text-[12px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1">{f.cost_note}</p>}
    {f.result?.message && <p data-testid={`bulk-file-result-${f.id}`} className="text-[12px] text-slate-700">{f.result.message}</p>}
    {(f.result?.warnings || []).map((w, i) => (
      <p key={i} data-testid={`bulk-file-warning-${f.id}-${i}`} className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">{w}</p>
    ))}
  </>
);

export const BulkFileRow = ({ f, onChanged }) => {
  // a scan's VIN only SUGGESTS — he taps the truck himself before Save is offered (his ruling 2026-10-20)
  const first = f.kind !== "sheet_scan" && (f.suggestions || []).length === 1 ? f.suggestions[0] : null;
  const [choice, setChoice] = useState(first);
  const [picker, setPicker] = useState(null);
  const [busy, setBusy] = useState("");
  const st = STATUS[f.status] || STATUS.refused;
  const open = f.status === "ready" || f.status === "needs_you";

  const act = async (what, body) => {
    setBusy(what);
    try {
      const { data } = await api.post(`/bulk-upload/files/${f.id}/${what}`, body || {});
      onChanged(data);
      toast.success(data.result?.message || "Done");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That did not work — nothing was changed.");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid={`bulk-file-${f.id}`} className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="w-4 h-4 text-slate-400" />
        <span data-testid={`bulk-file-name-${f.id}`} className="font-mono text-[12px] text-slate-800 break-all">{f.original_name}</span>
        {f.kind_label && <Badge data-testid={`bulk-file-kind-${f.id}`} className="rounded-md bg-slate-800 text-white text-[10px] normal-case">{f.kind_label}</Badge>}
        <Badge data-testid={`bulk-file-status-${f.id}`} className={`rounded-md border text-[10px] ${st.cls}`}>{st.label}</Badge>
      </div>
      <p data-testid={`bulk-file-why-${f.id}`} className="text-[13px] text-slate-600">{f.why}</p>
      <Preview f={f} />

      {open && (
        <>
          <Choices f={f} choice={choice} setChoice={setChoice} setPicker={setPicker} />
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button data-testid={`bulk-file-save-${f.id}`} disabled={!choice || !!busy} onClick={() => act("save", { target: choice.kind, id: choice.id })}
              className="h-10 rounded-lg bg-[#B01E28] hover:bg-[#8f1820] font-bold">
              {busy === "save" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
              {choice ? `Save to ${choice.kind === "part" ? "engine part" : "truck"} ${choice.label}` : "Pick where it goes"}
            </Button>
            {f.kind === "dataplate" && (
              <Button data-testid={`bulk-file-wait-${f.id}`} variant="outline" disabled={!!busy} onClick={() => act("wait")}
                className="h-10 rounded-lg"><Clock className="w-4 h-4 mr-1.5" />No match — put it on Waiting Dataplates</Button>
            )}
          </div>
        </>
      )}

      {(f.status === "saved" || f.status === "waiting") && (
        <div className="flex flex-wrap items-center gap-2">
          {f.saved_to && <span data-testid={`bulk-file-saved-to-${f.id}`} className="text-[12px] text-slate-600">On {f.saved_to.kind === "part" ? "engine part" : "truck"} <b>{f.saved_to.label}</b> · by {f.saved_by}</span>}
          <Button data-testid={`bulk-file-undo-${f.id}`} size="sm" variant="outline" disabled={!!busy} onClick={() => act("undo")} className="h-8 rounded-lg text-xs">
            {busy === "undo" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}Undo this file
          </Button>
        </div>
      )}
      {(f.status === "refused" || f.status === "no_match") && (
        <p data-testid={`bulk-file-nothing-${f.id}`} className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Ban className="w-3 h-3" />Nothing was read onto or saved to any truck from this file.</p>
      )}

      <TargetPicker open={!!picker} kind={picker} onClose={() => setPicker(null)}
        onPick={(t) => { setChoice({ kind: t.kind, id: t.id, label: t.label, why: "picked by you" }); setPicker(null); }} />
    </div>
  );
};
